const SANCTIFIED_SECRET_SPAWN_CHANCE = 0.07;

export const SANCTIFIED_STATUE_NAMES = {
  1: 'Knight',
  2: 'Mage',
  3: 'Archer',
  4: 'Paladin',
  5: 'Monk',
  6: 'Lancer',
  7: 'Miner',
  8: 'Mercenary',
  9: 'Druid',
  10: 'Bard',
};

const SECRET_TILE_RE = /^bookshelf-secret-r([01])-o([01])-c([01])-s(\d+)-x(\d+)-y(\d+)-([ab])$/;
const STATUE_TILE_RE = /^statue-(\d+)$/;
const SECRET_ANNEX_CHEST_TILES = new Set(['chest-rare-closed', 'chest-rare-locked']);

function cloneMap(map) {
  return Array.isArray(map) ? map.map((row) => row.slice()) : map;
}

function readBoolDigit(value) {
  return value === '1';
}

export function parseSanctifiedStatueId(tile) {
  const match = typeof tile === 'string' ? tile.match(STATUE_TILE_RE) : null;
  if (!match) return null;
  const statueId = Math.floor(Number(match[1]));
  return Number.isFinite(statueId) ? statueId : null;
}

export function isSanctifiedSecretLoreTile(tile) {
  return typeof tile === 'string' && SECRET_TILE_RE.test(tile);
}

export function parseSanctifiedSecretLoreTile(tile) {
  const match = typeof tile === 'string' ? tile.match(SECRET_TILE_RE) : null;
  if (!match) return null;

  const statueId = Math.floor(Number(match[4]));
  const targetX = Math.floor(Number(match[5]));
  const targetY = Math.floor(Number(match[6]));
  const shelfVariant = match[7] === 'b' ? 'b' : 'a';

  if (!Number.isFinite(statueId) || !Number.isFinite(targetX) || !Number.isFinite(targetY)) {
    return null;
  }

  return {
    read: readBoolDigit(match[1]),
    offered: readBoolDigit(match[2]),
    claimed: readBoolDigit(match[3]),
    statueId,
    targetX,
    targetY,
    shelfVariant,
  };
}

export function makeSanctifiedSecretLoreTile({
  read = false,
  offered = false,
  claimed = false,
  statueId,
  targetX,
  targetY,
  shelfVariant = 'a',
}) {
  const safeVariant = shelfVariant === 'b' ? 'b' : 'a';
  return [
    'bookshelf-secret',
    `r${read ? 1 : 0}`,
    `o${offered ? 1 : 0}`,
    `c${claimed ? 1 : 0}`,
    `s${Math.max(1, Math.floor(Number(statueId) || 1))}`,
    `x${Math.max(0, Math.floor(Number(targetX) || 0))}`,
    `y${Math.max(0, Math.floor(Number(targetY) || 0))}`,
    safeVariant,
  ].join('-');
}

function pickWithRng(rng, items) {
  if (!Array.isArray(items) || !items.length) return null;
  const roll = typeof rng === 'function' ? rng() : Math.random();
  const index = Math.max(0, Math.min(items.length - 1, Math.floor(roll * items.length)));
  return items[index] || null;
}

function collectBookshelfTiles(map) {
  const out = [];
  for (let y = 0; y < (map?.length || 0); y++) {
    for (let x = 0; x < (map?.[y]?.length || 0); x++) {
      const tile = map[y][x];
      if (tile === 'bookshelf-a' || tile === 'bookshelf-b') {
        out.push({ x, y, tile, shelfVariant: tile.endsWith('-b') ? 'b' : 'a' });
      }
    }
  }
  return out;
}

function isSecretAnnexStatue(map, x, y) {
  for (let dy = -1; dy <= 1; dy += 2) {
    for (let dx = -1; dx <= 1; dx += 2) {
      const tile = map?.[y + dy]?.[x + dx];
      if (SECRET_ANNEX_CHEST_TILES.has(tile)) return true;
    }
  }
  return false;
}

function collectStatueTiles(map) {
  const out = [];
  for (let y = 0; y < (map?.length || 0); y++) {
    for (let x = 0; x < (map?.[y]?.length || 0); x++) {
      const tile = map[y][x];
      const statueId = parseSanctifiedStatueId(tile);
      if (!statueId) continue;
      if (isSecretAnnexStatue(map, x, y)) continue;
      out.push({ x, y, tile, statueId });
    }
  }
  return out;
}

export function maybeApplySanctifiedStatueSecret(map, { moodId = null, rng = null } = {}) {
  if (!Array.isArray(map) || moodId !== 'sanctified') return map;

  const roll = typeof rng === 'function' ? rng() : Math.random();
  if (roll >= SANCTIFIED_SECRET_SPAWN_CHANCE) return map;

  const bookshelves = collectBookshelfTiles(map);
  const statues = collectStatueTiles(map);

  if (!bookshelves.length || !statues.length) return map;

  const chosenShelf = pickWithRng(rng, bookshelves);
  const chosenStatue = pickWithRng(rng, statues);
  if (!chosenShelf || !chosenStatue) return map;

  const next = cloneMap(map);
  next[chosenShelf.y][chosenShelf.x] = makeSanctifiedSecretLoreTile({
    read: false,
    offered: false,
    claimed: false,
    statueId: chosenStatue.statueId,
    targetX: chosenStatue.x,
    targetY: chosenStatue.y,
    shelfVariant: chosenShelf.shelfVariant,
  });
  return next;
}

export function getSanctifiedSecretLoreText(secretTileOrMeta) {
  const meta =
    typeof secretTileOrMeta === 'string'
      ? parseSanctifiedSecretLoreTile(secretTileOrMeta)
      : secretTileOrMeta;

  if (!meta) {
    return '“The ink has rotted into useless curls and stains.”';
  }

  const statueName = SANCTIFIED_STATUE_NAMES[meta.statueId] || 'Statue';
  return `“The ${statueName} hides something. Stone remembers more than flesh.”`;
}

export function getActiveSanctifiedStatueSecret(map) {
  for (let y = 0; y < (map?.length || 0); y++) {
    for (let x = 0; x < (map?.[y]?.length || 0); x++) {
      const meta = parseSanctifiedSecretLoreTile(map[y][x]);
      if (meta) {
        return {
          ...meta,
          loreX: x,
          loreY: y,
          targetKey: `${meta.targetX},${meta.targetY}`,
        };
      }
    }
  }
  return null;
}

function updateSecretTile(map, updater) {
  if (!Array.isArray(map)) return map;

  for (let y = 0; y < map.length; y++) {
    for (let x = 0; x < (map[y]?.length || 0); x++) {
      const meta = parseSanctifiedSecretLoreTile(map[y][x]);
      if (!meta) continue;
      const nextMeta = updater({ ...meta, loreX: x, loreY: y });
      if (!nextMeta) return map;
      const next = cloneMap(map);
      next[y][x] = makeSanctifiedSecretLoreTile(nextMeta);
      return next;
    }
  }

  return map;
}

export function markSanctifiedSecretLoreRead(map) {
  return updateSecretTile(map, (meta) => ({
    ...meta,
    read: true,
  }));
}

export function setSanctifiedSecretOfferPending(map, offered) {
  return updateSecretTile(map, (meta) => ({
    ...meta,
    offered: !!offered && !meta.claimed,
  }));
}

export function markSanctifiedSecretClaimed(map) {
  return updateSecretTile(map, (meta) => ({
    ...meta,
    read: true,
    offered: false,
    claimed: true,
  }));
}

export function isCurrentTileSanctifiedSecretTargetStatue(map, playerPos) {
  if (!playerPos) return false;

  const secret = getActiveSanctifiedStatueSecret(map);
  if (!secret || !secret.read || secret.claimed) return false;
  if (playerPos.x !== secret.targetX || playerPos.y !== secret.targetY) return false;

  const tile = map?.[playerPos.y]?.[playerPos.x];
  return parseSanctifiedStatueId(tile) === secret.statueId;
}
